import { formatCentsToUsd } from '@/utils/currency';
import { formatNumber } from '@/utils/number';

/** One fixed pack in a pack-priced purchase flow (Inbox Previews). */
export interface CreditsPurchasePack {
  /** Credits in the pack; also the value submitted as the purchase quantity. */
  size: number;
  priceCents: number;
}

/** Which legal terms a purchase agrees to. Inbox Previews' legal copy cites only the Terms of Service. */
export type CreditsPurchaseTermsLinks = 'feature-and-service' | 'service-only';

/**
 * Static configuration for a credit-purchase flow. AI credits and Builder credits share the same
 * form but differ in pricing, limits, copy, and whether a card can be saved.
 */
export interface CreditsPurchaseConfig {
  creditType: 'ai_credits' | 'builder_credits' | 'inbox_previews';
  pricePerUnitCents: number;
  creditsPerUnit?: number;
  defaultQuantity: number;
  minQuantity: number;
  maxQuantity: number;
  accountBalanceLimitCents?: number;
  quantityErrorLabel: string;
  quantityLabel: string;
  quantitySectionHeading: string;
  showCreditsBreakdown: boolean;
  creditsUnitLabel?: string;
  /** The fine print above the terms sentence: what is billed and that the purchase is final. */
  disclaimer: string;
  /** The terms the fine print cites; defaults to Feature Terms and Terms of Service. */
  termsLinks?: CreditsPurchaseTermsLinks;
  allowSaveCard: boolean;
  /** Submit button label; defaults to "Buy credits". */
  submitLabel?: string;
  /**
   * Fixed purchasable packs. When set, the quantity section renders a radio
   * picker instead of the number input, `quantity` holds the selected pack's
   * credit count, and the price comes from the pack rather than
   * `pricePerUnitCents` — pack pricing is non-linear, so a unit price cannot
   * express it.
   */
  packs?: CreditsPurchasePack[];
  /** Noun for pack rows, e.g. "+100 Inbox Previews"; defaults to the unit label. */
  packNoun?: string;
  snackbarPrefix: string;
  formatSuccessMessage: (totalPriceCents: number, totalCredits: number) => string;
}

/**
 * The largest quantity the account may purchase without exceeding its balance cap. Configs without an
 * `accountBalanceLimitCents` (AI credits) are uncapped and return `maxQuantity`. Clamped to `[0,
 * maxQuantity]` so a balance already at/over the cap yields 0.
 */
export function remainingPurchaseHeadroom(config: CreditsPurchaseConfig, currentBalanceCents: number): number {
  const { accountBalanceLimitCents, maxQuantity, pricePerUnitCents } = config;
  if (accountBalanceLimitCents == null) {
    return maxQuantity;
  }
  const remainingCents = accountBalanceLimitCents - currentBalanceCents;
  const maxFromBalance = Math.floor(remainingCents / pricePerUnitCents);
  return Math.min(maxQuantity, Math.max(0, maxFromBalance));
}

/**
 * Parses a quantity input's raw value into a rounded positive integer, or `null` when it isn't a
 * usable quantity yet. Guards on `>= 1` (not `config.minQuantity`) so a person can type multi-digit
 * values like "15" without the first keystroke "1" being rejected when the minimum is higher; range
 * validation is handled separately.
 */
export function parseQuantityInput(rawValue: string): number | null {
  const value = Math.round(Number.parseFloat(rawValue));
  if (Number.isNaN(value) || value < 1) {
    return null;
  }
  return value;
}

export const AI_CREDITS_CONFIG: CreditsPurchaseConfig = {
  creditType: 'ai_credits',
  pricePerUnitCents: 1000,
  creditsPerUnit: 100_000,
  defaultQuantity: 1,
  minQuantity: 1,
  maxQuantity: 100,
  quantityErrorLabel: 'credit packs',
  quantityLabel: 'Number of credit packs, 100,000 credits each',
  quantitySectionHeading: 'Credits',
  showCreditsBreakdown: true,
  creditsUnitLabel: 'credits',
  allowSaveCard: false,
  disclaimer: 'AI credits are non-refundable.',
  snackbarPrefix: 'ai-credits',
  formatSuccessMessage: (_totalPriceCents: number, totalCredits: number) =>
    `${formatNumber(totalCredits)} AI credits purchased.`,
};

export const BUILDER_CREDITS_CONFIG: CreditsPurchaseConfig = {
  creditType: 'builder_credits',
  pricePerUnitCents: 100,
  creditsPerUnit: undefined,
  defaultQuantity: 10,
  minQuantity: 10,
  maxQuantity: 1000,
  accountBalanceLimitCents: 100_000,
  quantityErrorLabel: 'amount',
  quantityLabel: 'Amount (USD)',
  quantitySectionHeading: 'Amount',
  showCreditsBreakdown: false,
  allowSaveCard: true,
  disclaimer: 'Added funds are non-refundable.',
  snackbarPrefix: 'builder-credits',
  formatSuccessMessage: (totalPriceCents: number) => `${formatCentsToUsd(totalPriceCents)} added to your account.`,
};

export const INBOX_PREVIEWS_CONFIG: CreditsPurchaseConfig = {
  creditType: 'inbox_previews',
  // Unused in packs mode (each pack carries its own price), but kept truthful:
  // the smallest pack works out to 25 cents per preview.
  pricePerUnitCents: 25,
  defaultQuantity: 100,
  minQuantity: 100,
  maxQuantity: 5000,
  quantityErrorLabel: 'pack',
  quantityLabel: 'Previews',
  quantitySectionHeading: 'Previews',
  showCreditsBreakdown: true,
  creditsUnitLabel: 'previews',
  allowSaveCard: false,
  // Legal-approved wording (CON-8130), used verbatim on both billing rails.
  disclaimer:
    'This purchase will be billed to your credit card on file. Inbox previews are non-refundable and non-transferable.',
  termsLinks: 'service-only',
  submitLabel: 'Buy previews',
  snackbarPrefix: 'inbox-previews',
  packNoun: 'Inbox previews',
  packs: [
    { size: 100, priceCents: 2500 },
    { size: 500, priceCents: 10_000 },
    { size: 2000, priceCents: 30_000 },
    { size: 5000, priceCents: 50_000 },
  ],
  formatSuccessMessage: (_totalPriceCents: number, totalCredits: number) =>
    `${formatNumber(totalCredits)} inbox previews purchased.`,
};
